import { ApiException, ErrorDetails, ProblemDetails } from 'src/app/services/api.services';
import { AppError, isNullOrUndefined } from 'src/app/shared/utils';

export namespace ErrorUtils {
  export function mergeErrorDetails(...errors: ReadonlyArray<ErrorDetails>): ErrorDetails {
    errors = errors.filter(x => !!x);

    if (!errors.length)
      return { title: 'Unexpected error' };

    if (errors.length == 1)
      return errors[0];

    return {
      title: `One or more errors occurred`,
      detail: errors.map(formatInner).join(`\r\n------------\r\n`)
    };

    function formatInner(x: ErrorDetails) {
      let text = [];

      let detail = [x.detail, x.exception].filter(x => !!x).join(`\r\n`);

      tryAppend(x.title);
      tryAppend(detail);
      tryAppend(x.svcRegion, 'svgRegion');
      tryAppend(x.svcMachine, 'svcMachine');
      tryAppend(x.correlationId, 'correlationId');
      tryAppend(x.traceId, 'traceId');

      return text.join(`\r\n`);

      function tryAppend(value: any, name?: string) {
        if (isNullOrUndefined(value) || !('' + value).trim().length) 
          return;

        if (name) 
          text.push(`${name}: ${value}`);
        else 
          text.push(value);
      }
    }
  }

  export function formatForUser(error: ApiException): string;
  export function formatForUser(error: AppError): string;
  export function formatForUser(error: Error): string;
  export function formatForUser(error: ErrorDetails): string;
  export function formatForUser(error: ProblemDetails): string;
  export function formatForUser(error: string): string;
  export function formatForUser(error: any): string {
    if (!error)
      return 'Unknown error';

    if (typeof error === 'string')
      return error;

    let text = [];

    if (error instanceof AppError) {
      if (error.data instanceof ApiException ||
        error.data instanceof Error) {
        return formatForUser(error.data);
      }

      tryAppend(error.status);
      tryAppend(error.message);
    }
    else
    if (error instanceof ApiException) {
      let name = [error.status, error.name].filter(x => !!x).join(' ');
      tryAppend(name);
      tryAppend(error.message);
      tryAppend(error.stack, 'Stack Trace');
      tryAppend(error.response, 'Response');
    }
    else if (error instanceof Error) {
      tryAppend(error.name);
      tryAppend(error.message);
      tryAppend(error.stack, 'Stack Trace');
    }
    else
    if (isErrorDetails(error)) {
      let detail = [error.detail, error.exception].filter(x => !!x).join(`\r\n`);

      tryAppend(error.title);
      tryAppend(detail);
      tryAppend(error.svcRegion, 'Region');
      tryAppend(error.svcMachine, 'Machine');
      tryAppend(error.correlationId, 'Correlation Id');
      tryAppend(error.traceId, 'Trace Id');
    }
    else
    if (isProblemDetails(error)) {
      let detail = [error.detail, error.exception].filter(x => !!x).join(`\r\n`);
      let type = [error.status, error.type].filter(x => !!x).join(' ');

      tryAppend(type);
      tryAppend(error.title);
      tryAppend(detail);
      tryAppend(error.instance, 'Instance');
    }
    else {
      if ('message' in error) 
        tryAppend(error.message);
      else if ('detail' in error)
        tryAppend(error.detail);
    }

    return text.join('\r\n');

    function tryAppend(value: any, name?: string) {
      if (isNullOrUndefined(value) || !('' + value).trim().length) 
        return;

      if (name) 
        text.push(`${name}: ${value}`);
      else 
        text.push(value);
    }
  }

  export function isErrorDetails(obj: any): obj is ErrorDetails {
    return !('type' in obj) && ('title' in obj && ('detail' in obj || 'exception' in obj));
  }

  export function isProblemDetails(obj: any): obj is ProblemDetails {
    return ('type' in obj && 'title' in obj) && (
      'status' in obj ||
      'detail' in obj ||
      'instance' in obj ||
      'extensions' in obj
    );
  }
}
